import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import Wiki from './Wiki';
import Bookmarks from './Bookmarks';

interface Company {
  id: number;
  name: string;
  email: string;
  company_name: string;
  phone: string;
  address: string;
}

interface User {
  id: number;
  email: string;
  role: string;
  name?: string;
  mfa_enabled?: boolean;
  mfa_method?: 'email' | 'totp';
  created_at: string;
}

interface Service {
  id: number;
  name: string;
  description: string;
  nrc: number;
  mrc: number;
  active: number;
  association_id?: number;
}

interface Ticket {
  id: number;
  ticket_id?: string;
  assigned_to_email?: string;
  subject: string;
  description: string;
  status: string;
  priority: string;
  created_at: string;
  is_outage?: number;
}

interface Invitation {
  id: number;
  email: string;
  role: string;
  created_at: string;
  expires_at: string;
}

interface CompanyProject {
  id: number;
  company_id: number | null;
  shepherd_id: number | null;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
  company_name?: string | null;
  shepherd_name?: string | null;
  shepherd_email?: string | null;
  total_tasks?: number;
  tasks_todo?: number;
  tasks_in_progress?: number;
  tasks_done?: number;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'open':
      return 'bg-yellow-100 text-yellow-800';
    case 'pending':
      return 'bg-blue-100 text-blue-800';
    case 'resolved':
      return 'bg-green-100 text-green-800';
    case 'closed':
      return 'bg-gray-100 text-gray-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'high':
      return 'bg-red-100 text-red-800';
    case 'medium':
      return 'bg-yellow-100 text-yellow-800';
    case 'low':
      return 'bg-green-100 text-green-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const CompanyDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser } = useAuth();
  
  // Get tab from URL or default to 'users'
  const queryParams = new URLSearchParams(location.search);
  const tabFromUrl = queryParams.get('tab') as any;
  // Valid tabs include wiki, bookmarks, projects for Global Users/Agents
  const validTabs = ['users', 'services', 'support', 'sso', 'wiki', 'bookmarks', 'projects'];
  const initialTab = (tabFromUrl && validTabs.includes(tabFromUrl)) ? tabFromUrl : 'users';
  
  const [company, setCompany] = useState<Company | null>(null);
  const [activeTab, setActiveTab] = useState<'users' | 'services' | 'support' | 'sso' | 'wiki' | 'bookmarks' | 'projects'>(initialTab);

  useEffect(() => {
    // Update active tab if URL changes
    const tabFromUrl = queryParams.get('tab') as any;
    const validTabs = ['users', 'services', 'support', 'sso', 'wiki', 'bookmarks', 'projects'];
    const tab = (tabFromUrl && validTabs.includes(tabFromUrl)) ? tabFromUrl : 'users';
    if (tab !== activeTab) {
      setActiveTab(tab);
    }
  }, [location.search]);

  const handleTabChange = (tab: 'users' | 'services' | 'support' | 'sso' | 'wiki' | 'bookmarks' | 'projects') => {
    setActiveTab(tab);
    navigate(`/companies/${id}?tab=${tab}`, { replace: true });
  };

  const [loading, setLoading] = useState(true);

  const isGlobalAdmin = currentUser?.role === 'admin' && !currentUser?.company_id;
  const isGlobalUser = !currentUser?.company_id; // Any global user (admin or customer role)
  const isCompanyAdmin = currentUser?.role === 'admin' && currentUser?.company_id && currentUser?.company_id.toString() === id;

  // Users Tab State
  const [users, setUsers] = useState<User[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [userFormData, setUserFormData] = useState({ email: '', password: '', name: '', role: 'customer' });
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteFormData, setInviteFormData] = useState({ email: '', role: 'customer' as 'admin' | 'customer' });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordUserId, setResetPasswordUserId] = useState<number | null>(null);
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);

  // MFA Management State
  const [mfaManagingUserId, setMfaManagingUserId] = useState<number | null>(null);
  const [showMfaEmailModal, setShowMfaEmailModal] = useState(false);
  const [showMfaTotpModal, setShowMfaTotpModal] = useState(false);
  const [mfaVerificationCode, setMfaVerificationCode] = useState('');
  const [mfaSendingCode, setMfaSendingCode] = useState(false);
  const [mfaVerifying, setMfaVerifying] = useState(false);
  const [mfaTotpSecret, setMfaTotpSecret] = useState('');
  const [mfaTotpQrCode, setMfaTotpQrCode] = useState('');
  const [mfaTotpManualKey, setMfaTotpManualKey] = useState('');
  const [mfaGeneratingTotp, setMfaGeneratingTotp] = useState(false);
  const [mfaError, setMfaError] = useState('');
  const [mfaSuccess, setMfaSuccess] = useState('');

  // MFA Required State
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaRequiredLoading, setMfaRequiredLoading] = useState(false);
  const [mfaRequiredMessage, setMfaRequiredMessage] = useState({ type: '', text: '' });

  // Services Tab State
  const [companyServices, setCompanyServices] = useState<Service[]>([]);
  const [allServices, setAllServices] = useState<Service[]>([]);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState('');

  // Support Tab State
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [ticketSortColumn, setTicketSortColumn] = useState<keyof Ticket | ''>('');
  const [ticketSortDirection, setTicketSortDirection] = useState<'asc' | 'desc'>('desc');
  const [ticketFilters, setTicketFilters] = useState({
    status: '',
    priority: '',
    search: '',
  });
  const [copiedTicketId, setCopiedTicketId] = useState<string | null>(null);
  const [showTicketColumnMenu, setShowTicketColumnMenu] = useState(false);

  // Column visibility state with localStorage persistence for company tickets
  const getDefaultTicketColumns = () => ({
    ticketId: true,
    subject: true,
    status: true,
    priority: true,
    created: true,
  });

  const [ticketColumnVisibility, setTicketColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('companyTicketColumns');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const defaults = getDefaultTicketColumns();
        return { ...defaults, ...parsed };
      } catch (e) {
        return getDefaultTicketColumns();
      }
    }
    return getDefaultTicketColumns();
  });

  const saveTicketColumnVisibility = (newVisibility: typeof ticketColumnVisibility) => {
    setTicketColumnVisibility(newVisibility);
    localStorage.setItem('companyTicketColumns', JSON.stringify(newVisibility));
  };

  const toggleTicketColumn = (column: keyof typeof ticketColumnVisibility) => {
    const newVisibility = { ...ticketColumnVisibility, [column]: !ticketColumnVisibility[column] };
    saveTicketColumnVisibility(newVisibility);
  };

  // Projects Tab State (internal projects for this company)
  const [companyProjects, setCompanyProjects] = useState<CompanyProject[]>([]);
  const [companyProjectsLoading, setCompanyProjectsLoading] = useState(false);
  const [companyProjectsError, setCompanyProjectsError] = useState<string | null>(null);

  // SSO Tab State
  const [ssoConfig, setSsoConfig] = useState({
    sso_enabled: false,
    sso_type: 'azure',
    sso_config: {
      tenantId: '',
      clientId: '',
      clientSecret: ''
    }
  });
  const [ssoLoading, setSsoLoading] = useState(false);
  const [ssoMessage, setSsoMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchCompanyData();
    if (isGlobalAdmin || isCompanyAdmin) {
      fetchSsoConfig();
    }
  }, [id]);

  useEffect(() => {
    if (activeTab === 'projects' && id && isGlobalUser) {
      fetchCompanyProjects();
    }
  }, [activeTab, id]);

  const fetchSsoConfig = async () => {
    try {
      const response = await api.get(`/companies/${id}/sso`);
      if (response.data) {
        setSsoConfig({
          sso_enabled: !!response.data.sso_enabled,
          sso_type: response.data.sso_type || 'azure',
          sso_config: response.data.sso_config || { tenantId: '', clientId: '', clientSecret: '' }
        });
      }
    } catch (error) {
      console.error('Error fetching SSO config:', error);
    }
  };

  const handleSsoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSsoLoading(true);
    setSsoMessage({ type: '', text: '' });
    try {
      await api.post(`/companies/${id}/sso`, ssoConfig);
      setSsoMessage({ type: 'success', text: 'SSO configuration updated successfully' });
    } catch (error: any) {
      setSsoMessage({ type: 'error', text: error.response?.data?.error || 'Failed to update SSO configuration' });
    } finally {
      setSsoLoading(false);
    }
  };

  const fetchCompanyData = async () => {
    try {
      setLoading(true);
      const [companyRes, usersRes, invitationsRes, servicesRes, allServicesRes, ticketsRes] = await Promise.all([
        api.get(`/companies/${id}`),
        api.get(`/companies/${id}/users`),
        api.get(`/companies/${id}/invitations`),
        api.get(`/companies/${id}/services`),
        api.get('/services'),
        api.get(`/companies/${id}/tickets`),
      ]);

      setCompany(companyRes.data);
      setUsers(usersRes.data);
      setInvitations(invitationsRes.data);
      setCompanyServices(servicesRes.data);
      setAllServices(allServicesRes.data);
      setTickets(ticketsRes.data);

      // Fetch MFA required status if global user
      if (isGlobalUser) {
        fetchMfaRequired();
      }
    } catch (error: any) {
      console.error('Error fetching company details:', error);
      const message = error.response?.data?.error || error.message || 'Unknown error';
      alert(`Error fetching company details: ${message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchMfaRequired = async () => {
    try {
      const response = await api.get(`/companies/${id}/mfa-required`);
      setMfaRequired(!!response.data.mfa_required);
    } catch (error: any) {
      console.error('Error fetching MFA required:', error);
    }
  };

  const fetchCompanyProjects = async () => {
    if (!id) return;
    const companyIdNum = parseInt(id, 10);
    if (Number.isNaN(companyIdNum)) return;
    setCompanyProjectsLoading(true);
    setCompanyProjectsError(null);
    try {
      const res = await api.get(`/companies/${id}/projects`);
      const list = res.data || [];
      // Ensure we only show projects for this company (defense in depth)
      const forCompany = list.filter((p: CompanyProject) => Number(p.company_id) === companyIdNum);
      setCompanyProjects(forCompany);
    } catch (err: any) {
      setCompanyProjectsError(err.response?.data?.error || err.message || 'Failed to load projects');
      setCompanyProjects([]);
    } finally {
      setCompanyProjectsLoading(false);
    }
  };

  const handleMfaRequiredSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMfaRequiredLoading(true);
    setMfaRequiredMessage({ type: '', text: '' });
    try {
      await api.post(`/companies/${id}/mfa-required`, { mfa_required: mfaRequired });
      setMfaRequiredMessage({ type: 'success', text: 'MFA requirement updated successfully' });
    } catch (error: any) {
      setMfaRequiredMessage({ type: 'error', text: error.response?.data?.error || 'Failed to update MFA requirement' });
    } finally {
      setMfaRequiredLoading(false);
    }
  };

  // --- User Handlers ---
  const handleUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingUser) {
        // Only send fields that are provided
        const updateData: any = { 
          email: userFormData.email, 
          role: userFormData.role 
        };
        if (userFormData.name) {
          updateData.name = userFormData.name;
        }
        if (userFormData.password) {
          updateData.password = userFormData.password;
        }
        await api.put(`/companies/${id}/users/${editingUser.id}`, updateData);
      } else {
        await api.post(`/companies/${id}/users`, userFormData);
      }
      setShowUserModal(false);
      fetchCompanyData();
    } catch (error: any) {
      console.error('Error saving user:', error);
      const message = error.response?.data?.error || error.response?.data?.errors?.map((e: any) => e.msg).join(', ') || error.message || 'Error saving user';
      alert(`Error saving user: ${message}`);
    }
  };

  const handleInviteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviteLoading(true);
    try {
      const response = await api.post(`/companies/${id}/invite`, inviteFormData);
      alert(response.data.message || 'Invitation sent successfully!');
      setShowInviteModal(false);
      setInviteFormData({ email: '', role: 'customer' });
      fetchCompanyData(); // Refresh the list
    } catch (error: any) {
      alert(error.response?.data?.error || 'Error sending invitation');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCancelInvite = async (invitationId: number) => {
    if (window.confirm('Are you sure you want to cancel this invitation?')) {
      try {
        await api.delete(`/companies/${id}/invitations/${invitationId}`);
        fetchCompanyData();
      } catch (error) {
        alert('Error canceling invitation');
      }
    }
  };

  // MFA Management Handlers
  const handleStartUserMfaEmail = async (userId: number) => {
    setMfaManagingUserId(userId);
    setMfaError('');
    setMfaSuccess('');
    setMfaSendingCode(true);
    try {
      await api.post(`/companies/${id}/users/${userId}/mfa/send-code`);
      setMfaSuccess('Verification code sent to user email');
      setShowMfaEmailModal(true);
    } catch (error: any) {
      setMfaError(error.response?.data?.error || 'Failed to send verification code');
    } finally {
      setMfaSendingCode(false);
    }
  };

  const handleStartUserMfaTotp = async (userId: number) => {
    setMfaManagingUserId(userId);
    setMfaError('');
    setMfaSuccess('');
    setMfaGeneratingTotp(true);
    try {
      const response = await api.post(`/companies/${id}/users/${userId}/mfa/totp/generate`);
      setMfaTotpSecret(response.data.secret);
      setMfaTotpQrCode(response.data.qrCode);
      setMfaTotpManualKey(response.data.manualEntryKey);
      setShowMfaTotpModal(true);
    } catch (error: any) {
      setMfaError(error.response?.data?.error || 'Failed to generate authenticator setup');
    } finally {
      setMfaGeneratingTotp(false);
    }
  };

  const handleEnableUserMfaEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaManagingUserId) return;
    
    setMfaVerifying(true);
    setMfaError('');
    setMfaSuccess('');
    try {
      await api.post(`/companies/${id}/users/${mfaManagingUserId}/mfa/enable-email`, {
        code: mfaVerificationCode
      });
      setMfaSuccess('Email MFA enabled successfully');
      setShowMfaEmailModal(false);
      setMfaVerificationCode('');
      setMfaManagingUserId(null);
      fetchCompanyData();
    } catch (error: any) {
      setMfaError(error.response?.data?.error || 'Failed to enable MFA');
    } finally {
      setMfaVerifying(false);
    }
  };

  const handleEnableUserMfaTotp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaManagingUserId) return;
    
    setMfaVerifying(true);
    setMfaError('');
    setMfaSuccess('');
    try {
      await api.post(`/companies/${id}/users/${mfaManagingUserId}/mfa/totp/verify`, {
        secret: mfaTotpSecret,
        code: mfaVerificationCode
      });
      setMfaSuccess('Authenticator app MFA enabled successfully');
      setShowMfaTotpModal(false);
      setMfaVerificationCode('');
      setMfaTotpSecret('');
      setMfaTotpQrCode('');
      setMfaTotpManualKey('');
      setMfaManagingUserId(null);
      fetchCompanyData();
    } catch (error: any) {
      setMfaError(error.response?.data?.error || 'Failed to enable MFA');
    } finally {
      setMfaVerifying(false);
    }
  };

  const handleDisableUserMfa = async (userId: number) => {
    if (!window.confirm('Are you sure you want to disable MFA for this user?')) {
      return;
    }
    
    setMfaError('');
    setMfaSuccess('');
    try {
      await api.post(`/companies/${id}/users/${userId}/mfa/disable`);
      setMfaSuccess('MFA disabled successfully');
      fetchCompanyData();
    } catch (error: any) {
      setMfaError(error.response?.data?.error || 'Failed to disable MFA');
    }
  };

  const deleteUser = async (userId: number) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await api.delete(`/companies/${id}/users/${userId}`);
        fetchCompanyData();
      } catch (error) {
        alert('Error deleting user');
      }
    }
  };

  const handleResetPassword = (userId: number) => {
    setResetPasswordUserId(userId);
    setResetPasswordValue('');
    setShowResetPasswordModal(true);
  };

  const handleSendPasswordReset = async (userId: number) => {
    if (!window.confirm('Send password reset email to this user?')) {
      return;
    }

    try {
      await api.post(`/companies/${id}/users/${userId}/send-password-reset`);
      alert('Password reset email sent successfully!');
    } catch (error: any) {
      console.error('Error sending password reset email:', error);
      const message = error.response?.data?.error || error.message || 'Unknown error';
      alert(`Error sending password reset email: ${message}`);
    }
  };

  const handleResetPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetPasswordUserId || !resetPasswordValue) {
      alert('Please enter a password');
      return;
    }

    if (resetPasswordValue.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }

    setResetPasswordLoading(true);
    try {
      await api.post(`/companies/${id}/users/${resetPasswordUserId}/reset-password`, { password: resetPasswordValue });
      setShowResetPasswordModal(false);
      setResetPasswordUserId(null);
      setResetPasswordValue('');
      alert('Password reset successfully!');
    } catch (error: any) {
      console.error('Error resetting password:', error);
      const message = error.response?.data?.error || error.message || 'Unknown error';
      alert(`Error resetting password: ${message}`);
    } finally {
      setResetPasswordLoading(false);
    }
  };

  // --- Service Handlers ---
  const associateService = async () => {
    try {
      await api.post(`/companies/${id}/services`, { service_id: parseInt(selectedServiceId) });
      setShowServiceModal(false);
      setSelectedServiceId('');
      fetchCompanyData();
    } catch (error) {
      alert('Error associating service');
    }
  };

  const removeService = async (associationId: number) => {
    if (window.confirm('Are you sure you want to remove this service association?')) {
      try {
        await api.delete(`/companies/${id}/services/${associationId}`);
        fetchCompanyData();
      } catch (error) {
        alert('Error removing service');
      }
    }
  };

  if (loading) return <div className="text-center py-12">Loading...</div>;
  if (!company) return <div className="text-center py-12">Company not found</div>;

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="mb-8">
        <button
          onClick={() => navigate('/companies')}
          className="mb-4 text-brand hover:opacity-90 text-sm font-medium flex items-center"
        >
          &larr; Back to Companies
        </button>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{company.company_name || company.name}</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">{company.email} | {company.phone}</p>
        <p className="text-gray-500 dark:text-gray-400">{company.address}</p>
      </div>

      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          {(['users', 'services', 'support', 'sso', 'wiki', 'bookmarks', 'projects'] as const)
            .filter(tab => {
              // Login Settings tab is visible to all global users (for MFA requirement) and global admins (for SSO)
              if (tab === 'sso' && !isGlobalUser) return false;
              // Wiki, Bookmarks, and Projects tabs are only visible to Global Users/Agents
              if ((tab === 'wiki' || tab === 'bookmarks' || tab === 'projects') && !isGlobalUser) return false;
              return true;
            })
            .map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabChange(tab)}
                className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab
                    ? 'border-brand text-brand'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                {tab === 'sso' ? 'Login Settings' : tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
        </nav>
      </div>

      <div className="mt-8">
        {activeTab === 'sso' && isGlobalUser && (
          <>
            {isGlobalAdmin && (
              <div className="max-w-2xl mb-8">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Single Sign-On Configuration</h2>
                
                <form onSubmit={handleSsoSubmit} className="space-y-6 bg-white dark:bg-gray-800 shadow sm:rounded-lg p-6">
                  {ssoMessage.text && (
                    <div className={`p-4 rounded-md ${ssoMessage.type === 'success' ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400' : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'}`}>
                      {ssoMessage.text}
                    </div>
                  )}

                  <div className="flex items-center">
                    <input
                      id="sso_enabled"
                      type="checkbox"
                      checked={ssoConfig.sso_enabled}
                      onChange={(e) => setSsoConfig({ ...ssoConfig, sso_enabled: e.target.checked })}
                      className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded"
                    />
                    <label htmlFor="sso_enabled" className="ml-2 block text-sm font-medium text-gray-900 dark:text-gray-100">
                      Enable SSO for this company
                    </label>
                  </div>

                  {ssoConfig.sso_enabled && (
                    <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">SSO Provider</label>
                        <select
                          value={ssoConfig.sso_type}
                          onChange={(e) => setSsoConfig({ ...ssoConfig, sso_type: e.target.value })}
                          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-brand focus:border-brand sm:text-sm rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        >
                          <option value="azure">Microsoft Azure AD (Entra ID)</option>
                          <option value="google">Google Workspace</option>
                        </select>
                      </div>

                      {ssoConfig.sso_type === 'azure' && (
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Tenant ID</label>
                            <input
                              type="text"
                              value={ssoConfig.sso_config.tenantId}
                              onChange={(e) => setSsoConfig({
                                ...ssoConfig,
                                sso_config: { ...ssoConfig.sso_config, tenantId: e.target.value }
                              })}
                              className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand focus:border-brand sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                              placeholder="Azure AD Tenant ID"
                              required={ssoConfig.sso_enabled}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Client ID</label>
                            <input
                              type="text"
                              value={ssoConfig.sso_config.clientId}
                              onChange={(e) => setSsoConfig({
                                ...ssoConfig,
                                sso_config: { ...ssoConfig.sso_config, clientId: e.target.value }
                              })}
                              className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand focus:border-brand sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                              placeholder="Azure AD Application (Client) ID"
                              required={ssoConfig.sso_enabled}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Client Secret</label>
                            <input
                              type="password"
                              value={ssoConfig.sso_config.clientSecret}
                              onChange={(e) => setSsoConfig({
                                ...ssoConfig,
                                sso_config: { ...ssoConfig.sso_config, clientSecret: e.target.value }
                              })}
                              className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand focus:border-brand sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                              placeholder="Azure AD Client Secret"
                              required={ssoConfig.sso_enabled}
                            />
                          </div>

                          <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-md">
                            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Instructions:</h4>
                            <ol className="text-xs text-gray-600 dark:text-gray-400 space-y-1 list-decimal ml-4">
                              <li>Go to the Azure Portal and navigate to Azure Active Directory.</li>
                              <li>Go to <strong>App registrations</strong> and create a new registration.</li>
                              <li>Set the redirect URI to: <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">{window.location.origin}/api/auth/sso/callback</code></li>
                              <li>Create a client secret and copy its value here.</li>
                            </ol>
                          </div>
                        </div>
                      )}

                      {ssoConfig.sso_type === 'google' && (
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Client ID</label>
                            <input
                              type="text"
                              value={ssoConfig.sso_config.clientId}
                              onChange={(e) => setSsoConfig({
                                ...ssoConfig,
                                sso_config: { ...ssoConfig.sso_config, clientId: e.target.value }
                              })}
                              className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand focus:border-brand sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                              placeholder="Google OAuth 2.0 Client ID"
                              required={ssoConfig.sso_enabled}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Client Secret</label>
                            <input
                              type="password"
                              value={ssoConfig.sso_config.clientSecret}
                              onChange={(e) => setSsoConfig({
                                ...ssoConfig,
                                sso_config: { ...ssoConfig.sso_config, clientSecret: e.target.value }
                              })}
                              className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-brand focus:border-brand sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                              placeholder="Google OAuth 2.0 Client Secret"
                              required={ssoConfig.sso_enabled}
                            />
                          </div>

                          <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-md">
                            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Instructions:</h4>
                            <ol className="text-xs text-gray-600 dark:text-gray-400 space-y-1 list-decimal ml-4">
                              <li>Go to the Google Cloud Console and select your project.</li>
                              <li>Go to <strong>APIs & Services</strong> &gt; <strong>Credentials</strong>.</li>
                              <li>Click <strong>Create Credentials</strong> &gt; <strong>OAuth client ID</strong>.</li>
                              <li>Select <strong>Web application</strong> as the application type.</li>
                              <li>Set the Authorized redirect URI to: <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">{window.location.origin}/api/auth/sso/callback</code></li>
                              <li>Copy the Client ID and Client Secret here.</li>
                            </ol>
                          </div>
                        </div>
                      )}

                      <div className="pt-5">
                        <button
                          type="submit"
                          disabled={ssoLoading}
                          className="w-full inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-brand hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand disabled:opacity-50"
                        >
                          {ssoLoading ? 'Saving...' : 'Save SSO Configuration'}
                        </button>
                      </div>
                    </div>
                  )}
                </form>
              </div>
            )}

            <div className="max-w-2xl">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">Multi-Factor Authentication Requirement</h2>
              
              <form onSubmit={handleMfaRequiredSubmit} className="space-y-6 bg-white dark:bg-gray-800 shadow sm:rounded-lg p-6">
                {mfaRequiredMessage.text && (
                  <div className={`p-4 rounded-md ${mfaRequiredMessage.type === 'success' ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400' : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'}`}>
                    {mfaRequiredMessage.text}
                  </div>
                )}

                <div className="flex items-center">
                  <input
                    id="mfa_required"
                    type="checkbox"
                    checked={mfaRequired}
                    onChange={(e) => setMfaRequired(e.target.checked)}
                    className="h-4 w-4 text-brand focus:ring-brand border-gray-300 dark:border-gray-600 rounded"
                  />
                  <label htmlFor="mfa_required" className="ml-2 block text-sm font-medium text-gray-900 dark:text-gray-100">
                    Require Multi-Factor Authentication for all users in this company
                  </label>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-4">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    When enabled, all users in this company will be required to set up and use Multi-Factor Authentication (MFA) before they can log in. Users who haven't enabled MFA will be prompted to set it up during login.
                  </p>
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={mfaRequiredLoading}
                    className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-brand hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand disabled:opacity-50"
                  >
                    {mfaRequiredLoading ? 'Saving...' : 'Save MFA Requirement'}
                  </button>
                </div>
              </form>
            </div>
          </>
        )}

        {activeTab === 'users' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Company Users</h2>
              {(isGlobalAdmin || isCompanyAdmin) && (
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      setEditingUser(null);
                      setUserFormData({ email: '', password: '', name: '', role: 'customer' });
                      setShowUserModal(true);
                    }}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-brand hover:bg-brand-hover"
                  >
                    Add User
                  </button>
                  {(isGlobalUser || isCompanyAdmin) && (
                    <button
                      onClick={() => {
                        setInviteFormData({ email: '', role: 'customer' });
                        setShowInviteModal(true);
                      }}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md shadow-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                    >
                      Invite User
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md mb-8">
              <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Active Users</h3>
              </div>
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {users.map((u) => (
                  <li key={u.id} className="px-4 py-4 sm:px-6 flex justify-between items-center">
                    <div>
                      <p className="text-sm font-medium text-brand">
                        {(u as any).name ? `${(u as any).name} (${u.email})` : u.email}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{u.role}</p>
                      {isGlobalUser && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          MFA: {u.mfa_enabled 
                            ? `${u.mfa_method === 'totp' ? 'Authenticator App' : 'Email'} enabled`
                            : 'Not enabled'}
                        </p>
                      )}
                    </div>
                    {(isGlobalAdmin || isCompanyAdmin) && (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            setEditingUser(u);
                            setUserFormData({ email: u.email, password: '', name: (u as any).name || '', role: u.role });
                            setShowUserModal(true);
                          }}
                          className="text-brand hover:opacity-90 text-sm font-medium"
                        >
                          Edit
                        </button>
                        {isGlobalUser && (
                          <>
                            <button
                              onClick={() => handleResetPassword(u.id)}
                              className="text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 text-sm font-medium"
                              title="Set password directly"
                            >
                              Reset Password
                            </button>
                            <button
                              onClick={() => handleSendPasswordReset(u.id)}
                              className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 text-sm font-medium"
                              title="Send password reset email"
                            >
                              Send Reset Email
                            </button>
                            {u.mfa_enabled ? (
                              <button
                                onClick={() => handleDisableUserMfa(u.id)}
                                className="text-orange-600 dark:text-orange-400 hover:text-orange-900 dark:hover:text-orange-300 text-sm font-medium"
                                title="Disable MFA for this user"
                              >
                                Disable MFA
                              </button>
                            ) : (
                              <>
                                <button
                                  onClick={() => handleStartUserMfaEmail(u.id)}
                                  className="text-purple-600 dark:text-purple-400 hover:text-purple-900 dark:hover:text-purple-300 text-sm font-medium"
                                  title="Enable Email MFA"
                                >
                                  Enable Email MFA
                                </button>
                                <button
                                  onClick={() => handleStartUserMfaTotp(u.id)}
                                  className="text-purple-600 dark:text-purple-400 hover:text-purple-900 dark:hover:text-purple-300 text-sm font-medium"
                                  title="Enable Authenticator App MFA"
                                >
                                  Enable TOTP MFA
                                </button>
                              </>
                            )}
                          </>
                        )}
                        <button
                          onClick={() => deleteUser(u.id)}
                          className="text-red-600 hover:text-red-900 text-sm font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </li>
                ))}
                {users.length === 0 && <li className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">No active users found</li>}
              </ul>
            </div>

            <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
              <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Pending Invitations</h3>
              </div>
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {invitations.map((inv) => (
                  <li key={inv.id} className="px-4 py-4 sm:px-6 flex justify-between items-center">
                    <div>
                      <p className="text-sm font-medium text-brand">{inv.email}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{inv.role} • Sent {new Date(inv.created_at).toLocaleDateString()}</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500">Expires {new Date(inv.expires_at).toLocaleDateString()}</p>
                    </div>
                    {(isGlobalAdmin || isCompanyAdmin) && (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            setInviteFormData({ email: inv.email, role: inv.role as 'admin' | 'customer' });
                            setShowInviteModal(true);
                          }}
                          className="text-brand hover:opacity-90 text-sm font-medium"
                        >
                          Resend
                        </button>
                        <button
                          onClick={() => handleCancelInvite(inv.id)}
                          className="text-red-600 dark:text-red-400 hover:text-red-900 text-sm font-medium"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </li>
                ))}
                {invitations.length === 0 && (
                  <li className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">No pending invitations found</li>
                )}
              </ul>
            </div>
          </div>
        )}

        {activeTab === 'services' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Associated Services</h2>
              {isGlobalAdmin && (
                <button
                  onClick={() => setShowServiceModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-brand hover:bg-brand-hover"
                >
                  Associate Service
                </button>
              )}
            </div>
            <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {companyServices.map((s) => (
                  <li key={s.association_id} className="px-4 py-4 sm:px-6 flex justify-between items-center">
                    <div>
                      <p className="text-sm font-medium text-brand">{s.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">NRC: ${s.nrc} | MRC: ${s.mrc}</p>
                    </div>
                    {isGlobalAdmin && (
                      <button
                        onClick={() => removeService(s.association_id!)}
                        className="text-red-600 hover:text-red-900 text-sm font-medium"
                      >
                        Remove
                      </button>
                    )}
                  </li>
                ))}
                {companyServices.length === 0 && <li className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">No services associated</li>}
              </ul>
            </div>
          </div>
        )}

        {activeTab === 'wiki' && isGlobalUser && (
          <Wiki companyId={id} />
        )}
        {activeTab === 'bookmarks' && isGlobalUser && (
          <Bookmarks companyId={id} />
        )}
        {activeTab === 'projects' && isGlobalUser && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Projects</h2>
              <button
                type="button"
                onClick={() => navigate('/internal/projects', { state: { newProjectCompanyId: id } })}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-brand hover:bg-brand-hover"
              >
                New project
              </button>
            </div>
            {companyProjectsError && (
              <div className="mb-4 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800">
                {companyProjectsError}
              </div>
            )}
            {companyProjectsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand" />
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead>
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Shepherd</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tasks</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Updated</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {companyProjects.map((proj) => (
                        <tr key={proj.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-4 py-3">
                            <button
                              type="button"
                              onClick={() => navigate(`/internal/projects/${proj.id}`)}
                              className="text-left font-medium text-brand hover:underline"
                            >
                              {proj.name}
                            </button>
                            {proj.description && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1 max-w-xs">{proj.description}</p>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                            {proj.shepherd_name || proj.shepherd_email || '—'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                            {Number(proj.total_tasks ?? 0)} total · To Do: {Number(proj.tasks_todo ?? 0)} · In Progress: {Number(proj.tasks_in_progress ?? 0)} · Done: {Number(proj.tasks_done ?? 0)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                            {new Date(proj.updated_at).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex gap-2 justify-end">
                              <button
                                type="button"
                                onClick={() => navigate(`/internal/projects/${proj.id}`)}
                                className="text-sm px-3 py-1.5 rounded border border-brand-border text-brand-muted-text hover:bg-brand-muted dark:hover:bg-brand-muted/20"
                              >
                                Open
                              </button>
                              <button
                                type="button"
                                onClick={async () => {
                                  if (!window.confirm('Delete this project and all its tasks?')) return;
                                  try {
                                    await api.delete(`/internal/projects/projects/${proj.id}`);
                                    fetchCompanyProjects();
                                  } catch (err: any) {
                                    alert(err.response?.data?.error || err.message || 'Failed to delete project');
                                  }
                                }}
                                className="text-sm px-3 py-1.5 rounded border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {companyProjects.length === 0 && !companyProjectsLoading && (
                  <p className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">No projects for this company yet. Create one to get started.</p>
                )}
              </div>
            )}
          </div>
        )}
        {activeTab === 'support' && (() => {
          const handleTicketSort = (column: keyof Ticket) => {
            if (ticketSortColumn === column) {
              setTicketSortDirection(ticketSortDirection === 'asc' ? 'desc' : 'asc');
            } else {
              setTicketSortColumn(column);
              setTicketSortDirection('asc');
            }
          };

          const copyToClipboard = async (text: string, ticketId: string) => {
            try {
              await navigator.clipboard.writeText(text);
              setCopiedTicketId(ticketId);
              setTimeout(() => setCopiedTicketId(null), 2000);
            } catch (err) {
              console.error('Failed to copy:', err);
              // Fallback for older browsers
              const textArea = document.createElement('textarea');
              textArea.value = text;
              textArea.style.position = 'fixed';
              textArea.style.left = '-999999px';
              document.body.appendChild(textArea);
              textArea.select();
              try {
                document.execCommand('copy');
                setCopiedTicketId(ticketId);
                setTimeout(() => setCopiedTicketId(null), 2000);
              } catch (err) {
                console.error('Fallback copy failed:', err);
              }
              document.body.removeChild(textArea);
            }
          };

          const getSortedAndFilteredTickets = () => {
            let filtered = [...tickets];

            // Apply filters
            if (ticketFilters.status) {
              filtered = filtered.filter(t => t.status === ticketFilters.status);
            }
            if (ticketFilters.priority) {
              filtered = filtered.filter(t => t.priority === ticketFilters.priority);
            }
            if (ticketFilters.search) {
              const searchLower = ticketFilters.search.toLowerCase();
              filtered = filtered.filter(t =>
                t.subject.toLowerCase().includes(searchLower) ||
                t.description.toLowerCase().includes(searchLower)
              );
            }

            // Apply sorting
            if (ticketSortColumn) {
              filtered.sort((a, b) => {
                let aVal: any = a[ticketSortColumn];
                let bVal: any = b[ticketSortColumn];

                // Handle date sorting
                if (ticketSortColumn === 'created_at') {
                  aVal = new Date(aVal).getTime();
                  bVal = new Date(bVal).getTime();
                } else if (typeof aVal === 'string') {
                  aVal = aVal.toLowerCase();
                  bVal = (bVal || '').toLowerCase();
                }

                if (aVal < bVal) return ticketSortDirection === 'asc' ? -1 : 1;
                if (aVal > bVal) return ticketSortDirection === 'asc' ? 1 : -1;
                return 0;
              });
            } else {
              // Default sort by created_at desc
              filtered.sort((a, b) => {
                const aTime = new Date(a.created_at).getTime();
                const bTime = new Date(b.created_at).getTime();
                return bTime - aTime;
              });
            }

            return filtered;
          };

          const sortedAndFilteredTickets = getSortedAndFilteredTickets();

          const TicketSortIcon = ({ column }: { column: keyof Ticket }) => {
            if (ticketSortColumn !== column) {
              return (
                <span className="ml-1 text-gray-400">
                  <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                  </svg>
                </span>
              );
            }
            return (
              <span className="ml-1 text-brand">
                {ticketSortDirection === 'asc' ? (
                  <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                )}
              </span>
            );
          };

          return (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Support Tickets</h2>
              
              {/* Filters */}
              <div className="mb-4 bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Search</label>
                    <input
                      type="text"
                      placeholder="Search tickets..."
                      value={ticketFilters.search}
                      onChange={(e) => setTicketFilters({ ...ticketFilters, search: e.target.value })}
                      className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-brand focus:border-brand"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                    <select
                      value={ticketFilters.status}
                      onChange={(e) => setTicketFilters({ ...ticketFilters, status: e.target.value })}
                      className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-brand focus:border-brand"
                    >
                      <option value="">All Statuses</option>
                      <option value="open">Open</option>
                      <option value="pending">Pending</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Priority</label>
                    <select
                      value={ticketFilters.priority}
                      onChange={(e) => setTicketFilters({ ...ticketFilters, priority: e.target.value })}
                      className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-brand focus:border-brand"
                    >
                      <option value="">All Priorities</option>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                  <div className="flex items-end">
                    <button
                      onClick={() => setTicketFilters({ status: '', priority: '', search: '' })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand"
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Tickets</h3>
                  <div className="relative">
                    <button
                      onClick={() => setShowTicketColumnMenu(!showTicketColumnMenu)}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                      </svg>
                      Columns
                    </button>
                    {showTicketColumnMenu && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setShowTicketColumnMenu(false)}></div>
                        <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg z-20 border border-gray-200 dark:border-gray-700">
                          <div className="py-1">
                            <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase border-b">Toggle Columns</div>
                            <label className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={ticketColumnVisibility.ticketId}
                                onChange={() => toggleTicketColumn('ticketId')}
                                className="rounded border-gray-300 text-brand focus:ring-brand"
                              />
                              <span className="ml-2 text-sm text-gray-700">Ticket ID</span>
                            </label>
                            <label className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={ticketColumnVisibility.subject}
                                onChange={() => toggleTicketColumn('subject')}
                                className="rounded border-gray-300 text-brand focus:ring-brand"
                              />
                              <span className="ml-2 text-sm text-gray-700">Subject</span>
                            </label>
                            <label className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={ticketColumnVisibility.status}
                                onChange={() => toggleTicketColumn('status')}
                                className="rounded border-gray-300 text-brand focus:ring-brand"
                              />
                              <span className="ml-2 text-sm text-gray-700">Status</span>
                            </label>
                            <label className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={ticketColumnVisibility.priority}
                                onChange={() => toggleTicketColumn('priority')}
                                className="rounded border-gray-300 text-brand focus:ring-brand"
                              />
                              <span className="ml-2 text-sm text-gray-700">Priority</span>
                            </label>
                            <label className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={ticketColumnVisibility.created}
                                onChange={() => toggleTicketColumn('created')}
                                className="rounded border-gray-300 text-brand focus:ring-brand"
                              />
                              <span className="ml-2 text-sm text-gray-700">Created</span>
                            </label>
                            <div className="border-t border-gray-200 mt-1">
                              <button
                                onClick={() => {
                                  const defaults = getDefaultTicketColumns();
                                  saveTicketColumnVisibility(defaults);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                              >
                                Reset to Default
                              </button>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {ticketColumnVisibility.ticketId && (
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Ticket ID
                          </th>
                        )}
                        {ticketColumnVisibility.subject && (
                          <th 
                            scope="col" 
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                            onClick={() => handleTicketSort('subject')}
                          >
                            <div className="flex items-center">
                              Subject
                              <TicketSortIcon column="subject" />
                            </div>
                          </th>
                        )}
                        {ticketColumnVisibility.status && (
                          <th 
                            scope="col" 
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                            onClick={() => handleTicketSort('status')}
                          >
                            <div className="flex items-center">
                              Status
                              <TicketSortIcon column="status" />
                            </div>
                          </th>
                        )}
                        {ticketColumnVisibility.priority && (
                          <th 
                            scope="col" 
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                            onClick={() => handleTicketSort('priority')}
                          >
                            <div className="flex items-center">
                              Priority
                              <TicketSortIcon column="priority" />
                            </div>
                          </th>
                        )}
                        {ticketColumnVisibility.created && (
                          <th 
                            scope="col" 
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                            onClick={() => handleTicketSort('created_at')}
                          >
                            <div className="flex items-center">
                              Created
                              <TicketSortIcon column="created_at" />
                            </div>
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {sortedAndFilteredTickets.length === 0 ? (
                        <tr>
                          <td colSpan={
                            [ticketColumnVisibility.ticketId, ticketColumnVisibility.subject,
                             ticketColumnVisibility.status, ticketColumnVisibility.priority,
                             ticketColumnVisibility.created].filter(Boolean).length
                          } className="px-6 py-8 text-center text-sm text-gray-500">
                            {tickets.length === 0 ? 'No tickets found' : 'No tickets match the current filters'}
                          </td>
                        </tr>
                      ) : (
                        sortedAndFilteredTickets.map((t) => {
                          const ticketId = t.ticket_id || `TKT-${String(t.id).padStart(6, '0')}`;
                          const isOutage = t.is_outage === 1;
                          return (
                            <tr key={t.id} className={`${isOutage ? 'bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500' : ''} hover:bg-gray-50`}>
                              {ticketColumnVisibility.ticketId && (
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center space-x-2">
                                    <Link
                                      to={`/tickets/${t.id}`}
                                      className="text-sm font-mono font-semibold text-brand hover:opacity-90 hover:underline"
                                    >
                                      {ticketId}
                                    </Link>
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        copyToClipboard(ticketId, ticketId);
                                      }}
                                      className="text-gray-400 hover:text-brand focus:outline-none"
                                      title="Copy Ticket ID to clipboard"
                                    >
                                      {copiedTicketId === ticketId ? (
                                        <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                      ) : (
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                      )}
                                    </button>
                                  </div>
                                </td>
                              )}
                              {ticketColumnVisibility.subject && (
                                <td className="px-6 py-4">
                                  <Link
                                    to={`/tickets/${t.id}`}
                                    className="text-sm font-medium text-brand hover:opacity-90 hover:underline"
                                  >
                                    {t.subject}
                                  </Link>
                                  <div className="text-xs text-gray-500 truncate max-w-xs">{t.description}</div>
                                  {t.assigned_to_email && (
                                    <div className="text-xs text-brand mt-1">Assigned: {t.assigned_to_email}</div>
                                  )}
                                </td>
                              )}
                              {ticketColumnVisibility.status && (
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(t.status)}`}>
                                    {t.status.replace('_', ' ')}
                                  </span>
                                </td>
                              )}
                              {ticketColumnVisibility.priority && (
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(t.priority)}`}>
                                    {t.priority}
                                  </span>
                                </td>
                              )}
                              {ticketColumnVisibility.created && (
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {new Date(t.created_at).toLocaleDateString()}
                                  <div className="text-xs text-gray-400">
                                    {new Date(t.created_at).toLocaleTimeString()}
                                  </div>
                                </td>
                              )}
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      {/* User Modal */}
      {showUserModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={() => setShowUserModal(false)}></div>
            </div>
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleUserSubmit}>
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100 mb-4">
                    {editingUser ? 'Edit User' : 'Add User'}
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Full Name</label>
                      <input
                        type="text"
                        required={!editingUser}
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        value={userFormData.name}
                        onChange={(e) => setUserFormData({ ...userFormData, name: e.target.value })}
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
                      <input
                        type="email"
                        required
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        value={userFormData.email}
                        onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Password {editingUser && '(leave blank to keep current)'}</label>
                      <input
                        type="password"
                        required={!editingUser}
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        value={userFormData.password}
                        onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Role</label>
                      <select
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        value={userFormData.role}
                        onChange={(e) => setUserFormData({ ...userFormData, role: e.target.value })}
                      >
                        <option value="customer">Customer</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button type="submit" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-brand text-base font-medium text-white hover:bg-brand-hover sm:ml-3 sm:w-auto sm:text-sm">Save</button>
                  <button type="button" onClick={() => setShowUserModal(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-600 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm">Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Invite User Modal */}
      {showInviteModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={() => setShowInviteModal(false)}></div>
            </div>
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleInviteSubmit}>
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100 mb-4">
                    Invite User
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email Address</label>
                      <input
                        type="email"
                        required
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-brand focus:border-brand"
                        value={inviteFormData.email}
                        onChange={(e) => setInviteFormData({ ...inviteFormData, email: e.target.value })}
                        placeholder="user@example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Role</label>
                      <select
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-brand focus:border-brand"
                        value={inviteFormData.role}
                        onChange={(e) => setInviteFormData({ ...inviteFormData, role: e.target.value as 'admin' | 'customer' })}
                      >
                        <option value="customer">Company User</option>
                        <option value="admin">Company Admin</option>
                      </select>
                    </div>
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
                      <p className="text-sm text-blue-800 dark:text-blue-200">
                        An invitation email will be sent to the user with a link to create their account.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={inviteLoading}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-brand text-base font-medium text-white hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {inviteLoading ? 'Sending...' : 'Send Invitation'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowInviteModal(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-600 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Service Modal */}
      {showServiceModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={() => setShowServiceModal(false)}></div>
            </div>
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100 mb-4">Associate Service</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Select Service</label>
                  <select
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3"
                    value={selectedServiceId}
                    onChange={(e) => setSelectedServiceId(e.target.value)}
                  >
                    <option value="">Choose a service...</option>
                    {allServices
                      .filter(s => !companyServices.some(cs => cs.id === s.id))
                      .map((s) => (
                        <option key={s.id} value={s.id}>{s.name} (${s.mrc}/mo)</option>
                      ))
                    }
                  </select>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  disabled={!selectedServiceId}
                  onClick={associateService}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-brand text-base font-medium text-white hover:bg-brand-hover sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  Associate
                </button>
                <button type="button" onClick={() => setShowServiceModal(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-600 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75 dark:bg-gray-900 dark:opacity-75" onClick={() => setShowResetPasswordModal(false)}></div>
            </div>
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleResetPasswordSubmit}>
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100 mb-4">
                    Reset User Password
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">New Password</label>
                      <input
                        type="password"
                        required
                        minLength={6}
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-brand focus:border-brand"
                        value={resetPasswordValue}
                        onChange={(e) => setResetPasswordValue(e.target.value)}
                        placeholder="Enter new password (min 6 characters)"
                      />
                    </div>
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
                      <p className="text-sm text-blue-800 dark:text-blue-200">
                        This will immediately change the user's password. They will need to use this new password to log in.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={resetPasswordLoading}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 dark:bg-green-500 text-base font-medium text-white hover:bg-green-700 dark:hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {resetPasswordLoading ? 'Resetting...' : 'Reset Password'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowResetPasswordModal(false);
                      setResetPasswordUserId(null);
                      setResetPasswordValue('');
                    }}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-600 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* MFA Email Setup Modal */}
      {showMfaEmailModal && mfaManagingUserId && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75 dark:bg-gray-900 dark:opacity-75" onClick={() => {
                setShowMfaEmailModal(false);
                setMfaManagingUserId(null);
                setMfaVerificationCode('');
              }}></div>
            </div>
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleEnableUserMfaEmail}>
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100 mb-4">
                    Enable Email MFA for User
                  </h3>
                  {(mfaError || mfaSuccess) && (
                    <div className={`mb-4 p-3 rounded-md ${mfaError ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400' : 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'}`}>
                      {mfaError || mfaSuccess}
                    </div>
                  )}
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      A verification code has been sent to the user's email. Enter the code below to enable Email MFA.
                    </p>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Verification Code</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]{6}"
                        maxLength={6}
                        required
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-brand focus:border-brand text-center text-2xl tracking-widest"
                        placeholder="000000"
                        value={mfaVerificationCode}
                        onChange={(e) => setMfaVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      />
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={mfaVerifying}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-brand text-base font-medium text-white hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand disabled:opacity-50 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {mfaVerifying ? 'Enabling...' : 'Enable Email MFA'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowMfaEmailModal(false);
                      setMfaManagingUserId(null);
                      setMfaVerificationCode('');
                      setMfaError('');
                      setMfaSuccess('');
                    }}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-600 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* MFA TOTP Setup Modal */}
      {showMfaTotpModal && mfaManagingUserId && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75 dark:bg-gray-900 dark:opacity-75" onClick={() => {
                setShowMfaTotpModal(false);
                setMfaManagingUserId(null);
                setMfaVerificationCode('');
                setMfaTotpSecret('');
                setMfaTotpQrCode('');
                setMfaTotpManualKey('');
              }}></div>
            </div>
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleEnableUserMfaTotp}>
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100 mb-4">
                    Enable Authenticator App MFA for User
                  </h3>
                  {(mfaError || mfaSuccess) && (
                    <div className={`mb-4 p-3 rounded-md ${mfaError ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400' : 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'}`}>
                      {mfaError || mfaSuccess}
                    </div>
                  )}
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      The user should scan this QR code with their authenticator app:
                    </p>
                    {mfaTotpQrCode && (
                      <div className="flex justify-center">
                        <img src={mfaTotpQrCode} alt="QR Code" className="border border-gray-300 dark:border-gray-600 rounded p-2 bg-white" />
                      </div>
                    )}
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        Or the user can enter this key manually:
                      </p>
                      <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded border border-gray-300 dark:border-gray-600">
                        <code className="text-sm font-mono text-gray-900 dark:text-gray-100 break-all">{mfaTotpManualKey}</code>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Enter the 6-digit code from the user's authenticator app
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]{6}"
                        maxLength={6}
                        required
                        className="block w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-brand focus:border-brand text-center text-2xl tracking-widest"
                        placeholder="000000"
                        value={mfaVerificationCode}
                        onChange={(e) => setMfaVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      />
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={mfaVerifying}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-brand text-base font-medium text-white hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand disabled:opacity-50 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {mfaVerifying ? 'Enabling...' : 'Enable Authenticator App MFA'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowMfaTotpModal(false);
                      setMfaManagingUserId(null);
                      setMfaVerificationCode('');
                      setMfaTotpSecret('');
                      setMfaTotpQrCode('');
                      setMfaTotpManualKey('');
                      setMfaError('');
                      setMfaSuccess('');
                    }}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-600 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyDetails;
